import React, { FunctionComponent, ReactNode, useCallback, useEffect, useState } from 'react';
import { Modal, Form, Row, Col, Input, Button, Typography, Select } from 'antd';
import { useTranslation } from 'react-i18next';
import { Store } from 'antd/es/form/interface';
import { Gutter } from 'antd/es/grid/row';
import { Close, User as UserIcon } from 'Components/icons';
import { ValidatedFormItem } from 'Components/validated-form-item';
import { useFormValidation, useService, useStores } from 'Hooks';
import { CreateMessageSchema } from 'Schemas';
import { CompanyService } from 'Services/CompanyService';
import { Company } from 'Models/Companies/Company';
import { CreateMessageRequestDto } from 'Api/Features/Messages/Dtos/CreateMessageRequestDto';
import { MessageReceiverTypeDto } from 'Api/Features/Messages/Dtos/MessageReceiverTypeDto';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import './create-message.less';
import { SelectCustom } from 'Components/select-custom';
import { SelectCustomOption } from 'Components/select-custom/select-custom';
import { theme } from 'variant';
import { CompanyMessageTypes } from 'Models/Messages/CompanyMessageTypes';

const { Text } = Typography;

const formGutter: [Gutter, Gutter] = [40, 0];

interface MessageModalProps {
    visible: boolean;
    onComplete: (success: boolean) => void;
    company?: Company;
    isEditing?: boolean;
    defaultMessageType: CompanyMessageTypes;
    messageTypeDisabled?: boolean;
}

const CreateCompanyMessageModal: FunctionComponent<MessageModalProps> = ({
    visible,
    onComplete,
    company,
    defaultMessageType,
    messageTypeDisabled
}) => {
    //#region Hooks
    const { t } = useTranslation();
    const [form] = Form.useForm();
    const [errors, validateForm, resetErrors] = useFormValidation(CreateMessageSchema, form);
    const { globalLoadingStore, toastStore, confirmationModalStore } = useStores();
    const companyService = useService(CompanyService);
    const [contentState, setContentState] = useState('');

    //#endregion

    //#region Options values

    //#endregion

    //#region Effects

    //#region Event handlers
    interface SelectOption {
        id: string;
        name: string;
        order?: number;
    }

    //#endregion

    const packageDeliveryPlaceholders = useCallback((): void => {
        form.setFieldsValue({
            title: t('Message.package_delivery_default_title'),
            body: t('Message.package_delivery_default_message'),
        });
        setContentState(t('Message.package_delivery_default_message'));
    }, [form, t]);

     useEffect(() => {
        if(defaultMessageType === CompanyMessageTypes.PackageDelivery){
            packageDeliveryPlaceholders();
        }
     }, [defaultMessageType, packageDeliveryPlaceholders])
     
    //#region Submit / Exit
    const dismiss = (success = false): void => {
        form.resetFields();
        resetErrors();
        onComplete(success);
    };

    const submit = async (values: Store): Promise<void> => {
        const newMessageData: CreateMessageRequestDto = {
            receiverType: MessageReceiverTypeDto.Company,
            receiverId: company?.id,
            type: values.type ?? defaultMessageType,
            entityType: null,
            entityId: null,
            title: values.title,
            body: values.body,
        };

        if (!(await validateForm(newMessageData, false))) return;
        try {
            globalLoadingStore.addLoading();
            await companyService.sendCompanyMessage(newMessageData);
            globalLoadingStore.removeLoading();
            await success();
        } catch (error) {
            toastStore.displayError(error);
        } finally {
            globalLoadingStore.removeLoading();
        }
    };
    //#endregion

    const success = async (): Promise<void> => {
        if (
            !(await confirmationModalStore.confirm({
                icon: <UserIcon />,
                title: t('Message.message_sent'),
                message: t('Message.message_company_sent_message'),
                positiveText: t('ok'),
            }))
        )
            return;
        dismiss(true);
    };

    const exit = async (): Promise<void> => {
        if (
            !(await confirmationModalStore.confirm({
                icon: <UserIcon />,
                title: t('Message.message_send_cancel_title'),
                message: t('Message.message_send_cancel_message'),
                positiveText: t('Message.message_send_cancel_positive'),
                negativeText: t('Message.message_send_cancel_negative'),
            }))
        )
            return;
        dismiss(false);
    };

    const handleMessageContentChange = (value: string): void => {
        setContentState(value);
        form.setFieldsValue({ body: value });
    };

    const companyOptionPlaceholder: SelectCustomOption[] = [
        {
            value: company?.id || '',
            label: company?.name || '',
            imageUrl: company?.imageUrl || undefined,
        },
    ];

    const messageTypeOptions = (): ReactNode => {
        return Object.values(CompanyMessageTypes).filter(type => type !== CompanyMessageTypes.Announcement).map((type) => (
            <Select.Option key={type} value={type}>
                {t(`MessageNotificationType.${type}`)}
            </Select.Option>
        ));
    }

    const handleTypeChange = (value: CompanyMessageTypes): void => {
        if(value === CompanyMessageTypes.PackageDelivery){
            packageDeliveryPlaceholders();
        }
    }
    
    return (
        <Modal
            visible={visible}
            centered
            title={t('Message.message_send_message')}
            className="FormModal"
            closeIcon={<Close />}
            width={960}
            footer={null}
            onCancel={(): Promise<void> => exit()}
            maskClosable={false}
        >
            <div className="CreateMessage">
                <Form layout="vertical" onFinish={submit} form={form}>
                    <Row>
                        <Col span={24} className="subTitle">
                            <Text className="subTitleText">
                                {t('Message.message_send_message_company_subtitle')}
                            </Text>
                        </Col>
                    </Row>
                    <Row gutter={formGutter}>
                        <Col span={8}>
                            <ValidatedFormItem
                                errors={errors}
                                name="type"
                                label={t('Message.message_type')}
                                required
                            >
                                <Select
                                    disabled={messageTypeDisabled}
                                    defaultValue={defaultMessageType}
                                    onChange={(value): void => handleTypeChange(value)}
                                >
                                    {messageTypeOptions()}
                                </Select>
                            </ValidatedFormItem>
                        </Col>
                        <Col span={8}>
                            <ValidatedFormItem
                                errors={errors}
                                name="title"
                                label={t('Message.message_title')}
                                required
                            >
                                <Input />
                            </ValidatedFormItem>
                        </Col>
                        <Col span={8}>
                            <ValidatedFormItem
                                errors={errors}
                                name="companyname"
                                label={t('Company.company')}
                            >
                                <SelectCustom
                                    options={companyOptionPlaceholder}
                                    selected={companyOptionPlaceholder.map(
                                        (option) => option.value
                                    )}
                                    defaultImg={<UserIcon fill={theme['white']} />}
                                    height="50px"
                                    disabled
                                />
                            </ValidatedFormItem>
                        </Col>
                    </Row>
                    <Row gutter={formGutter}>
                        <Col span={24}>
                            <ValidatedFormItem
                                errors={errors}
                                name="body"
                                label={t('Message.message_content')}
                                className="body"
                                required
                            >
                                <Input hidden />
                            </ValidatedFormItem>
                            <ReactQuill
                                theme="snow"
                                className="description-quill"
                                value={contentState}
                                onChange={handleMessageContentChange}
                            />
                        </Col>
                    </Row>
                    <div className="actions">
                        <Button
                            type="default"
                            className="secondary negative"
                            htmlType="button"
                            onClick={(): Promise<void> => exit()}
                        >
                            {t('cancel')}
                        </Button>
                        <Button type="primary" className="positive" htmlType="submit">
                            {t('Message.message_send')}
                        </Button>
                    </div>
                </Form>
            </div>
        </Modal>
    );
    //#endregion
};

export default React.memo(CreateCompanyMessageModal);
